export type InvoiceStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'paid'
export type InvoiceType = 'standard' | 'credit_note' | 'debit_note'
export type LineItemType = 'DR' | 'CR'
export type MatchingType = '2-way' | '3-way' | 'non-po'

export interface GoodsReceiptMatch {
  grNumber: string
  grDate: string
  grQuantity: number // Total quantity received in this GR
  allocatedQuantity: number // Quantity from this GR allocated to this invoice line
  grLineNumber?: number
  poNumber: string
  poLineNumber: number
}

export interface PurchaseOrderMatch {
  poNumber: string
  poLineNumber: number
  poLineDescription: string
  poQuantity: number
  poUnitPrice: number
  matchedQuantity: number
  remainingQuantity: number
  matchedAmount: number
  poDate?: string
  supplier?: string
}

export interface LineItem {
  id: string
  itemCode: string
  description: string
  quantity: number
  unitPrice: number
  subtotal: number
  taxCode: string
  taxAmt: number
  total: number
  type: LineItemType
  glCode?: string
  notes?: string
  poMatch?: PurchaseOrderMatch
  grMatch?: GoodsReceiptMatch // Deprecated: use grMatches for multi-GR support
  grMatches?: GoodsReceiptMatch[] // New: supports multiple GRs per line
}

export interface InvoiceDocument {
  id: string
  invoiceNumber: string
  supplier: string
  supplierABN?: string
  invoiceDate: string
  dueDate: string
  receivedDate?: string
  currency: string
  subtotal: number
  taxAmt: number
  total: number
  status: InvoiceStatus
  type: InvoiceType
  matchingType?: MatchingType
  poNumber?: string
  lineItems: LineItem[]
  notes?: string
  attachments?: string[]
  createdAt: string
  updatedAt: string
}

export interface Invoice {
  id: string
  documentType: string
  vendor: string
  documentNo: string
  documentDate: string
  dueDate: string
  poNo: string
  amount: string
  status:
    | 'COMPLIANCE'
    | 'FINAL REVIEW'
    | 'PO REVIEW'
    | 'PO INVOICE'
    | 'NON-PO INVOICE'
  team: string
  timestamp: string
  tag?: string
  matchingType?: 'non-po' | '2-way' | '3-way'
  receivedDate?: string
  lockedBy?: string // User currently editing this invoice
  lockedAt?: string // Timestamp when locked
}

export interface InvoiceDetails {
  entity: string
  site: string
  supplierCode: string
  vendorName: string
  vendorTaxNo: string
  email: string
  phoneNumber: string
  address: string
  city: string
  state: string
  country: string
  postcode: string
  vendorBankCheck: boolean
  // Bank details
  bankName?: string
  bankAccountName?: string
  bankAccountNumber?: string
  bankBSB?: string
  bankSwiftCode?: string
  bankIBAN?: string
  // Additional vendor details
  vendorCategory?: string
  paymentTerms?: string
  registrationDate?: string
  lastVerifiedDate?: string
  // ABR Lookup data (for Australian vendors)
  abrStatus?: string
  abrLastUpdatedDate?: string
  abrStatusEffectiveFrom?: string
  asicRegistration?: string
  abrEntityName?: string
  abrEntityType?: string
  gstEffectiveFrom?: string
  isDeductibleGiftRecipient?: string
  mainBusinessLocationPostcode?: string
  mainBusinessLocationStateCode?: string
}

export interface TaxCode {
  code: string
  meaning: string
  gstRate: string
  basEquivalent: string
  basTreatment: string
}

export const TAX_CODES: TaxCode[] = [
  {
    code: 'I',
    meaning: 'GST-inclusive taxable (price includes GST)',
    gstRate: '10%',
    basEquivalent: 'GST on Income / GST on Expenses',
    basTreatment: 'Taxable supplies (G1, 1A/1B)',
  },
  {
    code: 'E',
    meaning: 'Excluded from line-level tax (tax handled separately)',
    gstRate: '0% at line level',
    basEquivalent: 'BAS Excluded at line level',
    basTreatment:
      'Not reported at line level, tax handled at invoice/document level',
  },
  {
    code: 'Z',
    meaning: 'Zero-rated / GST-free sale or purchase',
    gstRate: '0%',
    basEquivalent: 'GST Free Income / GST Free Expenses',
    basTreatment: 'GST-free (G2/G3), no GST',
  },
  {
    code: 'R',
    meaning: 'Input taxed (e.g. residential rent, some financial)',
    gstRate: '0%',
    basEquivalent: 'Input Taxed',
    basTreatment: 'Input taxed supplies (no GST credits)',
  },
  {
    code: 'N',
    meaning: 'Not reportable / BAS excluded / out of scope',
    gstRate: '0%',
    basEquivalent: 'BAS Excluded',
    basTreatment: 'Not reported on BAS',
  },
]
